import clsx from "clsx";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import {
  UserOrg,
  useUserOrganizations,
} from "@/contexts/common/UserOrganizationsProvider";
import { UserRoles } from "@/enums/user";
import { useAlert } from "@/hooks/useAlert";
import { UserAuthDeleteResponse } from "@/types/user";
import { apiFetch } from "@/lib/api";
import { ClearAllCache } from "@/utils/common/cache";
import Button from "../library/Button";
import { Icon } from "../library/Icon";
import Modal from "../library/Modal";

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
};

const UserManagementModal = ({ show, setShow }: Props) => {
  const { role, user, switchOrg } = useAuthContext();
  const { orgs: userOrgs, refetch: refetchOrgs } = useUserOrganizations();
  const { setIsConfirming, setIsSaving, setAlert } = useAlert();
  const { t } = useTranslation("login");
  const { t: ct } = useTranslation("common");

  const [currentOrg, setCurrentOrg] = React.useState<string | null>(null);
  const [orgs, setOrgs] = React.useState<UserOrg[]>(userOrgs);

  useEffect(() => {
    if (user?.orgId) {
      setCurrentOrg(user.orgId);
    }
  }, [user?.orgId]);

  useEffect(() => {
    setOrgs(userOrgs);
  }, [userOrgs]);

  const handleRemove = async (org_id: string, agency_id: string) => {
    setIsSaving(true, t("removingYourAccess"));
    const agencyId = agency_id.includes("onboarding-")
      ? agency_id.replace("onboarding-", "")
      : agency_id;

    const response = await apiFetch<UserAuthDeleteResponse | { message: string }>(`/api/user-access/${org_id}/${agencyId}`, "DELETE");

    setIsSaving(false, "");

    if (response) {
      if (Array.isArray(response)) {
        // Refetch orgs from the API to get updated list
        await refetchOrgs();
      } else {
        setAlert({
          message: response.message,
          status: "error",
          secondsToClose: 10,
        });
      }
    } else {
      setAlert({
        message: t("failedToRemoveAccess"),
        status: "error",
        secondsToClose: 10,
      });
    }
  };

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && setShow(false)}
      header={{
        title: t("switchOrganization"),
        description: t("userManagementDescription"),
      }}
      buttons={[
        {
          type: "secondary",
          onClick: () => setShow(false),
          children: ct("close"),
        },
      ]}
    >
      <div>{`${t("accountLevel")}: ${t(role)}`}</div>
      {role !== UserRoles.Custom && (
        <div className={clsx("text-600", "text-xs")}>
          {t(role === UserRoles.Admin ? "adminDescription" : "userDescription")}
        </div>
      )}
      {orgs &&
        orgs.length > 1 &&
        orgs.map((o, i) => (
          <div key={i}>
            <hr className="my-4" />
            <div
              className={clsx(
                "mb-4",
                "grid",
                "gap-4",
                "grid-cols-[1fr_100px_46px]"
              )}
            >
              <div>
                <div className={clsx("text-xs", "text-600")}>{o.name}</div>
                {o.display_name}
              </div>
              <div>
                <Button
                  type={currentOrg === o.id ? "primary" : "secondary"}
                  disabled={currentOrg === o.id}
                  wide
                  onClick={() => {
                    ClearAllCache();
                    switchOrg(o.id);
                  }}
                >
                  {currentOrg === o.id ? t("current") : t("switch")}
                </Button>
              </div>
              <div>
                <Button
                  type="tertiary"
                  compact
                  onClick={async () => {
                    setIsConfirming(
                      true,
                      "delete",
                      t("removeYourAccess"),
                      async () => await handleRemove(o.id, o.name),
                      async () => {}
                    );
                  }}
                >
                  <Icon icon="trash" />
                </Button>
              </div>
            </div>
          </div>
        ))}
    </Modal>
  );
};

export default UserManagementModal;
